import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { 
  dealApi, 
  CreateDealRequest, 
  UpdateDealRequest, 
  Deal,
  ApiResponse 
} from '../api';
import { useT } from '../../../i18n';

// Query Keys
export const DEAL_QUERY_KEYS = {
  all: ['deals'] as const,
  lists: () => [...DEAL_QUERY_KEYS.all, 'list'] as const,
  list: (filters: string) => [...DEAL_QUERY_KEYS.lists(), { filters }] as const,
  details: () => [...DEAL_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...DEAL_QUERY_KEYS.details(), id] as const,
  vatCheck: (vatNumber: string) => [...DEAL_QUERY_KEYS.all, 'vat-check', vatNumber] as const,
};

// Hook to get all deals filtered by company VAT number
export const useGetAllDeals = (vatNumber?: string) => {
  return useQuery({
    queryKey: DEAL_QUERY_KEYS.list(vatNumber ?? 'all'),
    queryFn: () => dealApi.getAllDeals(vatNumber),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: vatNumber !== undefined, // only fetch once vatNumber is known
  });
};

// Hook to get deal by UUID
export const useGetDealByUuid = (uuid: string, enabled = true) => {
  return useQuery({
    queryKey: DEAL_QUERY_KEYS.detail(uuid),
    queryFn: () => dealApi.getDealByUuid(uuid),
    enabled: enabled && !!uuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to check VAT existence
export const useCheckVat = (vatNumber: string, enabled = false) => {
  return useQuery({
    queryKey: DEAL_QUERY_KEYS.vatCheck(vatNumber),
    queryFn: () => dealApi.checkVatExists(vatNumber),
    enabled: enabled && !!vatNumber && vatNumber.length > 0,
    staleTime: 0, // Always fresh for VAT checks
    retry: false,
  });
};

// Hook to create a new deal
export const useCreateDeal = () => {
  const t = useT();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDealRequest) => dealApi.createDeal(data),
    onSuccess: (response: ApiResponse<Deal>) => {
      // Invalidate and refetch deals list
      queryClient.invalidateQueries({ queryKey: DEAL_QUERY_KEYS.lists() });
      
      // Show success message
      toast.success(t('deals.toast.created'));
    },
    onError: (error: any) => {
      // Show error message
      const errorMessage = t('deals.toast.createFailed');
      toast.error(errorMessage);
    },
  });
};

// Hook to update a deal
export const useUpdateDeal = () => {
  const t = useT();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdateDealRequest }) => 
      dealApi.updateDeal(uuid, data),
    onSuccess: (response: ApiResponse<Deal>, variables) => {
      // Invalidate and refetch deals list
      queryClient.invalidateQueries({ queryKey: DEAL_QUERY_KEYS.lists() });
      
      // Update the specific deal in cache
      queryClient.invalidateQueries({ queryKey: DEAL_QUERY_KEYS.detail(variables.uuid) });
      
      // Show success message
      toast.success(t('deals.toast.updated'));
    },
    onError: (error: any) => {
      // Show error message
      const errorMessage = t('deals.toast.updateFailed');
      toast.error(errorMessage);
    },
  });
};

// Hook to delete a deal
export const useDeleteDeal = () => {
  const t = useT();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => dealApi.deleteDeal(uuid),
    onSuccess: (response: ApiResponse<void>) => {
      // Invalidate and refetch deals list
      queryClient.invalidateQueries({ queryKey: DEAL_QUERY_KEYS.lists() });
      
      // Show success message
      toast.success(t('deals.toast.deleted'));
    },
    onError: (error: any) => {
      // Show error message
      const errorMessage = t('deals.toast.deleteFailed');
      toast.error(errorMessage);
    },
  });
};

// Custom hook for VAT validation with debounce
export const useVatValidation = () => {
  const checkVatMutation = useMutation({
    mutationFn: (vatNumber: string) => dealApi.checkVatExists(vatNumber),
    onError: (error: any) => {
      console.error('VAT check failed:', error);
    },
  });

  const validateVat = async (vatNumber: string): Promise<boolean> => {
    if (!vatNumber || vatNumber.length < 5) {
      return false;
    }

    try {
      const response = await checkVatMutation.mutateAsync(vatNumber);
      return response.data.exists;
    } catch (error) {
      console.error('VAT validation error:', error);
      return false;
    }
  };

  return {
    validateVat,
    isValidating: checkVatMutation.isPending,
    error: checkVatMutation.error,
  };
};